import {
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  AfterViewInit,
} from "@angular/core";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import {
  ReportsService,
  Report,
  ReportsPagination,
} from "../../core/services/reports.service";
import { SnackbarService } from "../../core/services/snackbar.service";
import { CommonService } from "../../core/services/common.service";
import {
  TableColumn,
  PaginationConfig,
} from "../../shared/admin-common-table/admin-common-table.component";
import { DownloadDocumentsDialogComponent } from "../../shared/download-documents-dialog/download-documents-dialog.component";

@Component({
  selector: "app-report-history",
  templateUrl: "./report-history.component.html",
  styleUrls: ["./report-history.component.scss"],
})
export class ReportHistoryComponent implements OnInit, AfterViewInit {
  @ViewChild("viewReportTemplate") viewReportTemplate!: TemplateRef<any>;
  @ViewChild("downloadTemplate") downloadTemplate!: TemplateRef<any>;

  reports: Report[] = [];
  tableData: any[] = [];
  tableHeaders: TableColumn[] = [];

  paginationConfig: PaginationConfig = {
    totalItems: 0,
    currentPage: 1,
    itemsPerPage: 10,
    pageSizeOptions: [10, 25, 50],
    showPagination: true,
  };

  isLoading = false;

  constructor(
    private reportsService: ReportsService,
    private snackbarService: SnackbarService,
    private commonService: CommonService,
    private router: Router,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    // Initialize headers without template first
    this.tableHeaders = [
      { header: "Organization Name", field: "confirmedName" },
      { header: "PAN", field: "pan" },
      { header: "Date", field: "formattedDate" },
      { header: "Actions", field: "action", width: "200px" },
    ];

    this.loadReports();
  }

  ngAfterViewInit(): void {
    // Update the action column with the template after view init
    setTimeout(() => {
      this.tableHeaders = [
        { header: "Organization Name", field: "confirmedName" },
        { header: "PAN", field: "pan" },
        { header: "Date", field: "formattedDate" },
        {
          header: "Actions",
          field: "action",
          width: "200px",
          customTemplate: this.viewReportTemplate,
        },
      ];
    });
  }

  loadReports(): void {
    this.isLoading = true;
    this.reportsService
      .getPreviousReports({
        page: this.paginationConfig.currentPage,
        limit: this.paginationConfig.itemsPerPage,
        sortBy: "createdAt",
        sortOrder: "desc",
      })
      .subscribe({
        next: (response) => {
          this.reports = response.data.reports;
          this.tableData = this.reports.map((report) => ({
            ...report,
            formattedDate: this.commonService.dateFormatter(report.createdAt),
          }));
          this.paginationConfig = {
            ...this.paginationConfig,
            totalItems: response.pagination.total,
            currentPage: response.pagination.currentPage,
          };
          this.isLoading = false;
          this.snackbarService.showSuccess(
            response.message || "Reports loaded successfully"
          );
        },
        error: (error) => {
          const errorMessage = error.error?.message || "Failed to load reports";
          this.snackbarService.showError(errorMessage);
          this.isLoading = false;
        },
      });
  }

  onPageChange(event: { page: number; pageSize: number }): void {
    const pageChanged = this.paginationConfig.itemsPerPage === event.pageSize;
    this.paginationConfig = {
      ...this.paginationConfig,
      currentPage: pageChanged ? event.page : 1,
      itemsPerPage: event.pageSize,
    };
    this.loadReports();
  }

  viewReport(report: Report): void {
    this.router.navigate(["/ngo-diligence", report.transactionId]);
  }

  hasDownloadableDocuments(report: any): boolean {
    const hasMcaDocs = report.mcaDocuments?.documents?.length > 0;
    const hasLegalDocs = report.legalCheckDocuments?.documents?.length > 0;
    return hasMcaDocs || hasLegalDocs;
  }

  openDownloadDialog(report: any): void {
    this.dialog.open(DownloadDocumentsDialogComponent, {
      data: {
        confirmedName: report.confirmedName,
        pan: report.pan,
        mcaDocuments: report.mcaDocuments,
        legalCheckDocuments: report.legalCheckDocuments,
      },
      panelClass: 'download-documents-dialog',
      maxWidth: '90vw',
    });
  }
}
